import { AUTH_ORIGIN } from "../config";

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

export interface TokenStore {
  accessToken: string | null;
  refreshToken: string | null;
}

type RefreshFn = () => Promise<boolean>;

export function createApi(tokens: TokenStore, refresh: RefreshFn) {
  async function raw(path: string, init: RequestInit = {}, retryOn401 = true): Promise<Response> {
    const headers = new Headers(init.headers ?? {});
    if (tokens.accessToken && !headers.has("Authorization")) {
      headers.set("Authorization", `Bearer ${tokens.accessToken}`);
    }
    if (init.body && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
    const res = await fetch(`${AUTH_ORIGIN}${path}`, { ...init, headers });
    if (res.status === 401 && retryOn401 && tokens.refreshToken) {
      const ok = await refresh();
      if (ok) return raw(path, init, false);
    }
    return res;
  }

  async function json<T>(path: string, init: RequestInit = {}): Promise<T> {
    const res = await raw(path, init);
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const msg = (body as { error?: string }).error ?? `HTTP ${res.status}`;
      throw new ApiError(res.status, body, msg);
    }
    return body as T;
  }

  return { raw, json };
}

export type Api = ReturnType<typeof createApi>;
